import { EmailDeliveryEvent } from "@/types/EmailDeliveryEvent";

interface EventDetail {
  label: string;
  value: string;
}

/**
 * ONE date format for the whole detail panel: the timeline below and the
 * per-mail lines above it (W4-e portal / website visit) go through this
 * same formatter (audit W9-C lens 6 #14 — the lines used the browser's
 * `toLocaleString()` while the timeline fixed this format).
 */
export function formatTimestamp(ts: string | Date): string {
  try {
    const date = new Date(ts);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(ts);
  }
}

/**
 * A bounce is red only when it is PERMANENT. SES sends the same "Bounce" event
 * type for a dead mailbox and for a four-second deferral, and painting both red
 * is the same conflation the status ladder used to make.
 */
function getEventTypeColors(
  eventType: string | undefined,
  bounceType?: string,
): string {
  if (
    eventType?.toLowerCase() === "bounce" &&
    bounceType != null &&
    bounceType !== "Permanent"
  ) {
    return "user-icon-font-11 outline-user-icon-11 user-icon-bg-11";
  }
  switch (eventType?.toLowerCase()) {
    case "send":
      return "user-icon-font-2 outline-user-icon-2 user-icon-bg-2";
    case "delivery":
      return "user-icon-font-4 outline-user-icon-4 user-icon-bg-4";
    case "bounce":
      return "user-icon-font-3 outline-user-icon-3 user-icon-bg-3";
    case "complaint":
      return "user-icon-font-12 outline-user-icon-12 user-icon-bg-12";
    case "reject":
      return "user-icon-font-3 outline-user-icon-3 user-icon-bg-3";
    case "rendering failure":
      return "user-icon-font-12 outline-user-icon-12 user-icon-bg-12";
    case "deliverydelay":
      return "user-icon-font-11 outline-user-icon-11 user-icon-bg-11";
    case "open":
      return "user-icon-font-7 outline-user-icon-7 user-icon-bg-7";
    case "click":
      return "user-icon-font-10 outline-user-icon-10 user-icon-bg-10";
    case "subscription":
      return "user-icon-font-9 outline-user-icon-9 user-icon-bg-9";
    default:
      return "user-icon-font-9 outline-user-icon-9 user-icon-bg-9";
  }
}

/**
 * Whether this open/click came from a person or from a machine — a security
 * gateway prefetching every link, or a mailbox provider loading the tracking
 * pixel. Rendered in the same block as the user agent it was derived from, so
 * the verdict can be checked against its own evidence rather than taken on
 * faith.
 *
 * An absent verdict is NAMED, not omitted. Rendering it as "human" would be a
 * claim we cannot make — but staying silent left the SES row standing alone,
 * and a raw SES value like "false" then reads as "checked, not a bot" while
 * the dashboard counts the same event as unjudged.
 */
function pushOrigin(
  details: EventDetail[],
  t: (key: string) => string,
  entry: { machine?: boolean; machineReason?: string; isBotEvent?: string },
): void {
  details.push({
    label: t("engagementOrigin"),
    value:
      entry.machine == null
        ? t("engagementOriginUnknown")
        : entry.machine
          ? `${t("engagementOriginMachine")}${entry.machineReason ? ` (${entry.machineReason})` : ""}`
          : t("engagementOriginHuman"),
  });
  if (entry.isBotEvent) {
    details.push({ label: t("sesBotVerdict"), value: entry.isBotEvent });
  }
}


/**
 * The addresses this ONE event is about.
 *
 * SES keys its events by messageId, and one send carries To, Cc and Bcc under a
 * single id — so the envelope tells you nothing about which recipient an event
 * concerns. That answer exists, but only in the type-specific structure, and
 * only for the event types where SES actually knows it.
 */
function affectedRecipients(event: EmailDeliveryEvent): string[] {
  const data = event.eventData;
  switch (event.eventType?.toLowerCase()) {
    case "bounce":
      return addresses(data?.bounce?.bouncedRecipients);
    case "complaint":
      return addresses(data?.complaint?.complainedRecipients);
    case "delivery":
      return (data?.delivery?.recipients ?? []).filter(Boolean) as string[];
    case "deliverydelay":
      return addresses(data?.deliveryDelay?.delayedRecipients);
    default:
      return [];
  }
}

function addresses(list?: { emailAddress?: string }[]): string[] {
  return (list ?? [])
    .map((r) => r?.emailAddress)
    .filter((a): a is string => !!a);
}

/**
 * Event types that reach exactly one person but never say which.
 *
 * Open and click are measured by a tracking pixel and a link wrapper that
 * belong to the MESSAGE. When two people are on the envelope, SES cannot
 * attribute them and neither can we — and the guest rollup upstream stamps them
 * on the To: guest regardless, which is worth knowing when reading this panel.
 */
function isUnattributable(eventType: string | undefined): boolean {
  const t = eventType?.toLowerCase();
  return t === "open" || t === "click";
}

/** How each address was addressed, so a Cc bounce cannot read as a To bounce. */
export interface RecipientRoles {
  to?: string | null;
  cc?: string | null;
  bcc?: string | null;
  labels: { to: string; cc: string; bcc: string };
}

function describeRecipient(address: string, roles?: RecipientRoles): string {
  if (!roles) return address;
  const role = roleOf(address, roles);
  return role ? `${address} (${role})` : address;
}

function roleOf(address: string, roles: RecipientRoles): string | null {
  const needle = address.trim().toLowerCase();
  // cc/bcc are stored as single strings that the cockpit lets people fill with a
  // comma-separated list, so split before comparing.
  if (splitAddresses(roles.to).includes(needle)) return roles.labels.to;
  if (splitAddresses(roles.cc).includes(needle)) return roles.labels.cc;
  if (splitAddresses(roles.bcc).includes(needle)) return roles.labels.bcc;
  return null;
}

function splitAddresses(raw?: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean);
}

export function getEventDetails(
  event: EmailDeliveryEvent,
  t: (key: string) => string,
  roles?: RecipientRoles,
): EventDetail[] {
  const details: EventDetail[] = [];
  const eventType = event.eventType?.toLowerCase();

  // Common top-level fields
  if (event.timestamp)
    details.push({
      label: t("timestamp"),
      value: formatTimestamp(event.timestamp),
    });

  if (event.source)
    details.push({ label: t("source"), value: event.source });

  // WHO this event is about, when SES says so.
  //
  // This row used to be event.destination — SES's mail.destination, which is
  // the whole envelope (To + Cc + Bcc) and therefore IDENTICAL on every event
  // of a message. On a send with a Cc it answered the wrong question: it said
  // who the mail went to, never who bounced. The real recipient lives one level
  // down and in a different place per event type.
  const affected = affectedRecipients(event);
  if (affected.length) {
    details.push({
      label: t("affectedRecipient"),
      value: affected.map((a) => describeRecipient(a, roles)).join(", "),
    });
  } else if (isUnattributable(event.eventType)) {
    // Opens and clicks carry NO recipient — the tracking pixel and the link
    // wrapper are per MESSAGE, not per recipient. On a shared envelope there is
    // no way to tell whether the To: or the Cc: person opened it. Saying so is
    // the honest rendering; printing the envelope here would look like an answer.
    details.push({
      label: t("affectedRecipient"),
      value: t("affectedRecipientUnattributable"),
    });
  }

  if (event.destination?.length)
    details.push({
      label: t("destination"),
      value: event.destination
        .map((a) => describeRecipient(a, roles))
        .join(", "),
    });

  if (event.subject)
    details.push({ label: t("subject"), value: event.subject });

  // Event-specific details
  if (eventType === "send" && event.eventData?.send) {
    details.push({
      label: t("success"),
      value: event.eventData.send.success ? t("yes") : t("no"),
    });
  } else if (eventType === "delivery" && event.eventData?.delivery) {
    const delivery = event.eventData.delivery;
    if (delivery.smtpResponse)
      details.push({ label: t("smtpResponse"), value: delivery.smtpResponse });
    if (delivery.processingTimeMillis != null)
      details.push({
        label: t("processingTime"),
        value: `${delivery.processingTimeMillis} ms`,
      });
  } else if (eventType === "bounce" && event.eventData?.bounce) {
    const bounce = event.eventData.bounce;
    if (bounce.bounceType)
      details.push({ label: t("bounceType"), value: bounce.bounceType });
    if (bounce.bounceSubType)
      details.push({ label: t("bounceSubType"), value: bounce.bounceSubType });
    if (bounce.feedbackId)
      details.push({ label: t("feedbackId"), value: bounce.feedbackId });
    if (bounce.bouncedRecipients?.length) {
      const diagCode = bounce.bouncedRecipients[0]?.diagnosticCode;
      if (diagCode)
        details.push({ label: t("diagnosticCode"), value: diagCode });
    }
  } else if (eventType === "complaint" && event.eventData?.complaint) {
    const complaint = event.eventData.complaint;
    if (complaint.complaintFeedbackType)
      details.push({
        label: t("complaintFeedbackType"),
        value: complaint.complaintFeedbackType,
      });
    if (complaint.userAgent)
      details.push({ label: t("userAgent"), value: complaint.userAgent });
  } else if (eventType === "reject" && event.eventData?.reject) {
    if (event.eventData.reject.reason)
      details.push({ label: t("reason"), value: event.eventData.reject.reason });
  } else if (
    eventType === "deliverydelay" &&
    event.eventData?.deliveryDelay
  ) {
    const delay = event.eventData.deliveryDelay;
    if (delay.delayType)
      details.push({ label: t("delayType"), value: delay.delayType });
    if (delay.expirationTime)
      details.push({
        label: t("expirationTime"),
        value: formatTimestamp(delay.expirationTime),
      });
  } else if (
    eventType === "rendering failure" &&
    event.eventData?.renderingFailure
  ) {
    const rf = event.eventData.renderingFailure;
    if (rf.templateName)
      details.push({ label: t("templateName"), value: rf.templateName });
    if (rf.errorMessage)
      details.push({ label: t("errorMessage"), value: rf.errorMessage });
  }

  // Tracking details (clicks / opens) - if tracking data exists
  if (event.tracking?.clicks?.length) {
    for (const click of event.tracking.clicks) {
      if (click.userAgent)
        details.push({ label: t("userAgent"), value: click.userAgent });
      // W4-e ⚑J: the parser replaces a bearer secret in the clicked URL
      // (`/p/<jwt>`, `token=`) with `[redacted]` and flags the entry. Such a
      // link is shown as a placeholder, never as the string and never as an
      // empty row — the reader must see that a click happened AND that its
      // target was withheld on purpose.
      if (click.linkRedacted === true || (click.link && click.link.includes("[redacted]")))
        details.push({ label: t("url"), value: t("urlRedacted") });
      else if (click.link)
        details.push({ label: t("url"), value: click.link });
      if (click.ipAddress)
        details.push({ label: t("ipAddress"), value: click.ipAddress });
      pushOrigin(details, t, click);
    }
  }

  if (event.tracking?.opens?.length) {
    for (const open of event.tracking.opens) {
      if (open.userAgent)
        details.push({ label: t("userAgent"), value: open.userAgent });
      if (open.ipAddress)
        details.push({ label: t("ipAddress"), value: open.ipAddress });
      pushOrigin(details, t, open);
    }
  }

  return details;
}

export const EmailDeliveryEventCard: React.FC<{
  event: EmailDeliveryEvent;
  t: (key: string) => string;
  roles?: RecipientRoles;
}> = ({ event, t, roles }) => {
  const details = getEventDetails(event, t, roles);
  const bounceType = event.eventData?.bounce?.bounceType;
  const eventTypeLabel = event.eventType
    ? bounceType != null &&
      bounceType !== "Permanent" &&
      event.eventType.toLowerCase() === "bounce"
      ? t("bounceTransient")
      : t(event.eventType.toLowerCase())
    : "";
  const colorClasses = getEventTypeColors(event.eventType, bounceType);

  return (
    <div className="flex flex-row gap-3 p-4 my-1.5 mx-2 border outline-quaternary rounded-xl">
      <div className="w-[9.5rem] shrink-0 flex items-start pt-0.5">
        <p
          className={`body-2-bold px-3 py-1 border rounded-xl text-center break-words ${colorClasses} outline-quaternary`}
        >
          {eventTypeLabel}
        </p>
      </div>
      <div className="flex flex-col gap-3 min-w-0 flex-1">
        {details.map((detail, i) => (
          <div key={i} className="flex flex-col">
            <p className="body-2-bold font-primary">{detail.label}</p>
            <p className="body-2-regular font-secondary wrap-anywhere">
              {detail.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
